import { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Loader2, Upload, Wine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const AUTH_TEXT = "I authorize Luxury Rideshare to use my card on file to buy these products for me.";
export const CARD_REQUIRED = "A credit card must be on file to order food or alcohol";
export const emptyFood = () => ({ food_note: "", includes_alcohol: false, id_document_id: null, age_confirmed: false, authorized: false, filename: "" });
export const foodPayload = (f) => ({ food_note: f.food_note, includes_alcohol: f.includes_alcohol, id_document_id: f.id_document_id, age_confirmed: f.age_confirmed, authorized: f.authorized });
const hasRequest = (f) => !!f.food_note.trim() || f.includes_alcohol;
export const foodBlocked = (f, user) => hasRequest(f) && (!user?.card_on_file || !f.authorized || (f.includes_alcohol && (!f.id_document_id || !f.age_confirmed || (f.id_check && f.id_check.check_status !== "ok"))));

const AddCard = () => {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try { window.location.href = (await api.post("/payments/setup", { origin_url: window.location.origin })).data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div data-testid="food-card-required" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
      <p className="text-sm font-medium text-amber-200">{CARD_REQUIRED}</p>
      <button type="button" data-testid="food-add-card-button" onClick={start} disabled={busy} className="btn-gold mt-3 !py-2 text-sm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Add card
      </button>
    </div>
  );
};

const AlcoholId = ({ value, set }) => {
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/id-uploads", fd);
      set({ id_document_id: data.id, filename: data.filename, id_check: data });
      if (data.check_status === "ok") toast.success(data.message); else toast.error(data.message);
    } catch (err) { setUploadError(errMsg(err)); toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-3 rounded-lg border border-amber-500/20 p-3">
      <label className="btn-outline-gold w-full min-w-0 !justify-start !px-4 !py-2.5 text-sm cursor-pointer">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        <span className="truncate">{value.filename ? `ID: ${value.filename}` : "Upload the front of your driver's license"}</span>
        <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" data-testid="id-upload-input" className="hidden" onChange={upload} />
      </label>
      {uploadError && <p data-testid="id-upload-error" className="text-xs font-medium text-red-300">{uploadError}</p>}
      {value.id_check && (
        <div data-testid="id-check-result" className={`rounded-md border p-2.5 text-xs ${value.id_check.check_status === "ok" ? "border-emerald-500/30 text-emerald-300" : "border-red-500/40 text-red-300"}`}>
          <p data-testid="id-check-message" className="font-semibold">{value.id_check.message}</p>
          {value.id_check.extracted?.date_of_birth && (
            <p className="mt-1 text-zinc-400" data-testid="id-check-fields">Born {value.id_check.extracted.date_of_birth} (age {value.id_check.age}) · expires {value.id_check.extracted.expiry_date || "—"}</p>
          )}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-zinc-300">
        <input type="checkbox" data-testid="age-confirm-checkbox" checked={value.age_confirmed} onChange={(e) => set({ age_confirmed: e.target.checked })} /> I am 21 or older
      </label>
      {(!value.id_document_id || !value.age_confirmed) && <p data-testid="id-required-error" className="text-xs font-medium text-amber-500">ID required for alcohol requests</p>}
    </div>
  );
};

export const FoodRequest = ({ value, onChange }) => {
  const { user } = useAuth();
  const set = (patch) => onChange({ ...value, ...patch });
  const active = hasRequest(value);
  return (
    <div className="lux-card space-y-3 p-5 sm:p-6" data-testid="food-request">
      <label className="lux-eyebrow block">Food & beverage request (optional)</label>
      <textarea data-testid="food-note-input" maxLength={500} className="lux-input min-h-[70px]" placeholder="e.g. sparkling water, charcuterie board" value={value.food_note} onChange={(e) => set({ food_note: e.target.value })} />
      <label className="flex items-center gap-2 text-sm text-zinc-300">
        <input type="checkbox" data-testid="includes-alcohol-checkbox" checked={value.includes_alcohol} onChange={(e) => set({ includes_alcohol: e.target.checked })} />
        <Wine className="h-4 w-4 text-amber-400" /> Includes alcohol
      </label>
      {active && !user?.card_on_file && <AddCard />}
      {active && user?.card_on_file && value.includes_alcohol && <AlcoholId value={value} set={set} />}
      {active && user?.card_on_file && (
        <label className="flex items-start gap-2 text-sm text-zinc-300">
          <input type="checkbox" className="mt-1" data-testid="food-authorize-checkbox" checked={value.authorized} onChange={(e) => set({ authorized: e.target.checked })} />
          <span>{AUTH_TEXT}</span>
        </label>
      )}
      <p className="text-xs text-zinc-500">No in-app food pricing — we arrange it and charge the actual cost to your card on file.</p>
    </div>
  );
};

export const FoodSummary = ({ trip }) => {
  const food = trip.food;
  if (!food) return null;
  const p = trip.purchase;
  return (
    <div data-testid="food-summary" className="lux-card space-y-2 p-5 text-sm sm:p-6">
      <p className="lux-eyebrow">Food & beverage request</p>
      {food.note && <p data-testid="food-note-text" className="text-zinc-200">{food.note}</p>}
      {food.includes_alcohol && <p data-testid="food-id-status" className="text-xs text-zinc-400">Alcohol · {food.id_status === "verified" ? "ID verified" : food.id_status === "rejected" ? "ID rejected — alcohol declined" : "ID pending review"}</p>}
      {food.driver_id_check && <p data-testid="food-driver-id-check" className={`text-xs ${food.alcohol_handover === "not_handed_over" ? "text-red-300" : "text-emerald-300"}`}>{food.alcohol_handover === "not_handed_over" ? "Driver: ID doesn't match — alcohol not handed over" : "Driver checked physical ID — 21+ and matches"}</p>}
      {food.authorization && <p className="text-xs text-zinc-500">Authorized: “{food.authorization.text}”</p>}
      {p && (
        <div data-testid="food-purchase" className={`flex justify-between border-t border-white/5 pt-2 ${p.status === "paid" ? "text-zinc-200" : "text-red-300"}`}>
          <span>{p.label}{p.note ? ` · ${p.note}` : ""} {p.status !== "paid" && <span data-testid="food-purchase-due">· Payment due</span>}</span>
          <span data-testid="food-purchase-amount">{money(p.amount)}</span>
        </div>
      )}
      {p?.receipt_id && <Link to={`/receipts/${p.receipt_id}`} data-testid="food-purchase-receipt-link" className="inline-block text-xs text-amber-300 hover:text-amber-200">View receipt →</Link>}
    </div>
  );
};

export const ChargePurchase = ({ trip, onDone }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);
  if (!trip.food || trip.purchase?.status === "paid") return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let receipt_photo_id = null;
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        receipt_photo_id = (await api.post("/admin/receipt-photos", fd)).data.photo_id;
      }
      const { data } = await api.post(`/admin/trips/${trip.id}/purchase`, { amount: Number(amount), note, receipt_photo_id });
      data.purchase?.status === "paid" ? toast.success("Purchase charged — receipt sent") : toast.error("Payment due");
      onDone(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="charge-purchase-form" className="lux-card space-y-3 p-5 sm:p-6">
      <p className="lux-eyebrow">Charge purchase</p>
      <input type="number" min="0.01" step="0.01" required data-testid="purchase-amount-input" className="lux-input" placeholder="Amount ($)" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <input required maxLength={500} data-testid="purchase-note-input" className="lux-input" placeholder="Receipt note (items bought)" value={note} onChange={(e) => setNote(e.target.value)} />
      <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400">
        <Upload className="h-4 w-4 text-amber-400" />
        <span className="truncate" data-testid="purchase-photo-name">{photo ? photo.name : "Attach store receipt photo (optional)"}</span>
        <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="purchase-photo-input" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
      </label>
      <button type="submit" disabled={busy || !amount || !note.trim()} data-testid="purchase-charge-button" className="btn-gold">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Charge card on file</button>
    </form>
  );
};
